/**
 * The thin per-surface step from `lib/outreach-stage.ts`'s SHAPES to strings, for web.
 *
 * Time params come from EXISTING formatters (Q13), never a new one: `{ago}` is `agoRelativeTime` with
 * `outreach.time.justNow` as the under-a-minute label and `contacts.time.ago` as the template, `{day}` is
 * the activity-clock rule `activityTime` (today → time, ≤ 6 days → weekday + time, older → month + day),
 * and `{countdown}` is the module's own `m:ss`. `{maestroAgentName}` is passed in by the caller
 * (`useAgentName().name`) — web's catalog layer does not fill it.
 *
 * Pure (no React), so the components and the tests share it.
 */
import { agoRelativeTime } from './contact-rows';
import { activityTime } from './notification-time';
import { countdown, type StageLabel, type StageLine, type StageParam } from './outreach-stage';

export type Translate = (key: string, params?: Record<string, unknown>) => string;

export interface StageRenderContext {
  t: Translate;
  maestroAgentName: string;
  now?: Date;
  locale?: string;
}

/**
 * `{day}` / `{time}` — the activity-clock rule (rule 8): the same calendar day → time; within six days →
 * weekday + time; else month + day. Past instants go through `activityTime` unchanged. A FUTURE instant
 * that is not today must still name its day ("Sends at Tue 09:00", never a bare "09:00" for tomorrow),
 * which `activityTime` — written for the past — does not do, so it is handled here.
 */
export function stageDayLabel(iso: string, now: Date, locale?: string): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  if (then <= now.getTime()) return activityTime(iso, now.getTime(), locale);
  const d = new Date(then);
  const time = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((startOfDay(d) - startOfDay(now)) / 86_400_000);
  if (days === 0) return time;
  if (days <= 6) return `${d.toLocaleDateString(locale, { weekday: 'short' })} ${time}`;
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

/** Lines whose time param is the whole point of the sentence ("… since {day}"): with no timestamp they
 *  fall back to the no-time variant, the reused `tasks.status.waiting`, rather than "since " over nothing. */
const NO_TIME_FALLBACK: Record<string, string> = {
  'outreach.line.waitingOn': 'tasks.status.waiting',
  'outreach.line.noReplySince': 'tasks.status.waiting',
};

export function renderStageParam(param: StageParam, ctx: StageRenderContext): string {
  const now = ctx.now ?? new Date();
  switch (param.kind) {
    case 'text':
      return param.value;
    case 'key':
      return ctx.t(param.key, { maestroAgentName: ctx.maestroAgentName });
    case 'day':
      return param.at ? stageDayLabel(param.at, now, ctx.locale) : '';
    case 'ago':
      return agoRelativeTime(param.at || undefined, now, ctx.t('outreach.time.justNow'), (time) => ctx.t('contacts.time.ago', { time }));
    case 'countdown':
      return countdown(param.at ?? undefined, now);
  }
}

function renderParams(params: Record<string, StageParam>, ctx: StageRenderContext): Record<string, string> {
  const out: Record<string, string> = { maestroAgentName: ctx.maestroAgentName };
  for (const [name, param] of Object.entries(params)) out[name] = renderStageParam(param, ctx);
  return out;
}

/** A row line as a string; a suffix renders as `line + " · " + t(suffix)`. */
export function renderStageLine(line: StageLine, ctx: StageRenderContext): string {
  if (line.kind === 'text') return line.text;
  const timeless = Object.values(line.params).some((param) => (param.kind === 'day' || param.kind === 'ago') && !param.at);
  if (timeless && NO_TIME_FALLBACK[line.key]) {
    const fallback = ctx.t(NO_TIME_FALLBACK[line.key]);
    return line.suffix ? `${fallback} · ${ctx.t(line.suffix)}` : fallback;
  }
  const body = ctx.t(line.key, renderParams(line.params, ctx));
  return line.suffix ? `${body} · ${ctx.t(line.suffix)}` : body;
}

/** A key-only label (a speaker, the thread header) as a string. */
export function renderStageLabel(label: StageLabel, ctx: StageRenderContext): string {
  return ctx.t(label.key, renderParams(label.params, ctx));
}

/** The contact screen's `outreach.line.withGroup {group, line}`. */
export function renderWithGroupLine(
  line: { key: string; params: { group: { kind: 'key'; key: string }; line: StageLine } },
  ctx: StageRenderContext,
): string {
  return ctx.t(line.key, { group: ctx.t(line.params.group.key), line: renderStageLine(line.params.line, ctx) });
}
